import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  MdAlignHorizontalLeft,
  MdOutlinePayments,
  MdThumbDownOffAlt,
} from "react-icons/md";
import { FaChartBar, FaMagnifyingGlass } from "react-icons/fa6";
import { IoDocumentOutline, IoPeopleSharp } from "react-icons/io5";
import AdminPaymentsTable from "../components/tables/AdminPaymentsTable";
import AdminHistoryTable from "../components/tables/AdminHistoryTable";
import AdminReportSurveysTable from "../components/tables/AdminReportSurveysTable";
import AdminHistoryGeneralTable from "../components/tables/AdminHistoryGeneralTable";
import { BsCashCoin } from "react-icons/bs";
import AdminCloseTable from "../components/tables/AdminCloseTable";
import { useApplication } from "../contexts/ApplicationContext";
import { LiaSellsy } from "react-icons/lia";
import AdminHistoryTableProviders from "../components/tables/AdminHistoryTableProviders";
import { VscDebugConsole } from "react-icons/vsc";
import AdminLogErrorsProviders from "../components/tables/AdminLogErrorsProviders";
import AdminCVsTable from "../components/tables/AdminCVsTable";
import { GrDocumentTransfer } from "react-icons/gr";
import ReviewDiario from "./ReviewDiarioPage";
import AdminNegativeTable from "../components/tables/AdminNegativeTable";
import ModularResponseTimeChart from "../components/graphs/ModularResponseTimeChart";

const ReportsPage = () => {
  const { userPermission, user } = useApplication();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const tabs = useMemo(() => {
    const items = [
      {
        id: "consultas",
        title: "Consultas",
        icon: FaMagnifyingGlass,
        render: (isActive) => <AdminHistoryTable isActive={isActive} />,
      },
      {
        id: "negativacoes",
        title: "Negativações",
        icon: MdThumbDownOffAlt,
        render: (isActive) => <AdminNegativeTable isActive={isActive} />,
      },
    ];

    if (
      userPermission === "POWER_USER" ||
      user?.Company?.companyTypeId === "REPRESENTANTE"
    ) {
      items.push({
        id: "provedores",
        title: "Consumo de Provedores",
        icon: LiaSellsy,
        render: (isActive) => (
          <AdminHistoryTableProviders isActive={isActive} />
        ),
      });
    }

    if (userPermission === "POWER_USER") {
      items.push({
        id: "logs",
        title: "Log de erros",
        icon: VscDebugConsole,
        render: (isActive) => <AdminLogErrorsProviders isActive={isActive} />,
      });
      items.push({
        id: "response-time",
        title: "Tempo de resposta",
        icon: FaChartBar,
        render: () => <ModularResponseTimeChart />,
      });
    }

    return items;
  }, [userPermission, user?.Company?.companyTypeId]);

  useEffect(() => {
    if (activeTab >= tabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, tabs.length]);

  return (
    <>
      <div className="bg-gradient-to-br from-white via-slate-50 to-white px-4 py-4 rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-200/70 relative">
        <div
          className="flex items-center gap-2 overflow-x-auto pb-1.5 -mx-1 px-1"
          role="tablist"
          aria-label="Relatórios"
        >
          <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/80 p-1 shadow-sm">
            {tabs.map((tab, idx) => {
              const Icon = tab.icon;
              const isActive = idx === activeTab;
              return (
                <button
                  key={tab.id}
                  id={`reports-tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`reports-panel-${tab.id}`}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveTab(idx)}
                  className={[
                    "group inline-flex items-center gap-2 rounded-full px-3 py-2 text-[11px] sm:text-sm font-semibold whitespace-nowrap transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2",
                    isActive
                      ? "bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-lg shadow-slate-500/30 ring-slate-900/70 scale-[1.02]"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "flex items-center justify-center h-6 w-6 rounded-full transition-colors",
                      isActive
                        ? "bg-white/15 text-white ring-1 ring-white/30"
                        : "bg-slate-100 text-slate-600 group-hover:bg-slate-200",
                    ].join(" ")}
                  >
                    <Icon className="text-[12px] sm:text-[14px]" />
                  </span>
                  <span>{tab.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-3">
          {tabs[activeTab] && (
            <div
              id={`reports-panel-${tabs[activeTab].id}`}
              role="tabpanel"
              aria-labelledby={`reports-tab-${tabs[activeTab].id}`}
            >
              {tabs[activeTab].render(true)}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReportsPage;
